/**
 * Back that means back.
 *
 * The voucher screen used to bind Escape to `nav('/')` and label it
 * "back". That is Home wearing Back's label, and it breaks the one path
 * the product is built around: Outstanding -> ledger -> voucher. Press
 * Escape after drilling three levels down and you were returned to the
 * home screen, having lost the report you were reading.
 *
 * `nav(-1)` alone is not the fix either -- on the first screen after
 * sign-in, or on a deep link someone was sent, the previous history entry
 * belongs to another site, and "back" would walk the user out of the app.
 *
 * React Router gives the initial history entry the key "default" and a
 * fresh key to every entry pushed after it. So a "default" key means
 * there is nowhere inside the app to go back to, and Home is the honest
 * destination.
 */
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

export function useSmartBack(): { back: () => void; canGoBack: boolean } {
  const nav = useNavigate()
  const loc = useLocation()
  const canGoBack = loc.key !== 'default'

  const back = useCallback(() => {
    if (canGoBack) nav(-1)
    else nav('/')
  }, [canGoBack, nav])

  return { back, canGoBack }
}
